import axios from 'axios';

/**
 * Saves the registrationModel by calling the API. If the registrationModel 
 * has a userRegistrationId, then this User Registration is already known to the 
 * server and so a save will be called immediately. If the userRegistrationId is 
 * missing, the create action will be called first, which will return the ID. The 
 * submit save action will then be called to persist the supplied registrationModel.
 * 
 * @param {object} registrationModel The UserRegistrationModel to supply to the server. Should be an object copy, and not the actual reference to the model.
 * @param {object} componentConfig The component configuration of the current UserRegistration instance.
 * @param {number} currentStep The currentStep of the entire component that the user has reached.
 */
export async function saveRegistrationModel(registrationModel, componentConfig, currentStep) {

    // This is a copy of some of the data of registrationModel that will be manually constructed for submission to the server.
    const registrationModelRequest = {
        userRegistrationId: registrationModel.userRegistrationId
    }

    // If we do not have a userRegistrationId, that means we need to first create the registration on the server.
    if (!registrationModelRequest.userRegistrationId) {

        const createConfig = {
            method: 'POST',
            url: '/public/user-registration',
            data: {context: componentConfig.context, contactId: componentConfig.contactId},
            headers: {
                'Content-Type': 'application/json'
            }
        }

        await axios.request(createConfig).then((response) => {

            if (response.data) {
                registrationModelRequest.userRegistrationId = response.data;
            }

        });

    }

    // For each section that the user has already completed (and the current section), we want to add this to the information we will submit to be saved.
    componentConfig.activeComponents.forEach((componentName) => {

        // Compare each section component's sequence with the currentStep the user has reached.
        if (componentConfig.componentDefinition[componentName].sequence <= currentStep) {
            const modelKey = componentConfig.componentDefinition[componentName].props.modelKey;
            registrationModelRequest[modelKey] = registrationModel[modelKey];
        }

    });

    // Add all "secondary" subscription item ids to the same list. The backend will take care of the rest.
    if (componentConfig.isPCEnabled && registrationModel.selectedSecondarySubscriptions) {
        let selectedSecondarySubscriptionIds = registrationModel.selectedSecondarySubscriptions.map(i => i.id);
        registrationModelRequest['subscription'].secondarySubscriptionItemIds = selectedSecondarySubscriptionIds;
    }

    // The save dto needs to be aware of the context to determine how to validate certain sections
    registrationModelRequest.registrationContext = registrationModel.registrationContext;
    registrationModelRequest.enableABTesting = componentConfig.settings.enableABTesting;
    registrationModelRequest.informations = registrationModel.informations;

    // If we are A/B testing stuff, we need to send this over as well so it gets persisted in the DB.
    // It needs to be persisted because we want to know if AB testing was enabled when the confirmation
    // page loads which uses what we have stored in the DB.
    if (componentConfig.settings.enableABTesting && componentConfig.settings.enableABTesting === true) {
        
        Object.keys(componentConfig.settings.abTestSettings).forEach(function(key) {
            // key: the name of the object key
            // index: the ordinal position of the key within the object 
            const optionName = key;
            const optionValue = componentConfig.settings.abTestSettings[key];
            
            registrationModelRequest.informations.push({
                name: optionName, 
                value: optionValue
            });
        });
    }

    // Now prepare to save the model.
    const saveConfig = {
        method: 'PUT',
        url: '/public/user-registration/' + registrationModelRequest.userRegistrationId,
        data: registrationModelRequest,
        headers: {
            'Content-Type': 'application/json'
        }
    }

    return await axios.request(saveConfig).then((response) => {
        return response;
    }).catch((error) => {
        console.error(error);
        return error;
    });

}

/**
 * Finalizes the user registration process.
 * 
 * @param {number} userRegistrationId 
 */
 export async function finalizeUserRegistration(userRegistrationId) {
    
    const config = {
        method: 'POST',
        url: '/public/user-registration/' + userRegistrationId + '/finalize',
        headers: {
            'Content-Type': 'application/json'
        },
        data: {
            'userRegistrationId': userRegistrationId
        }
    };

    return await axios.request(config).then((response) => {
        return response;
    }, (error) => {
        console.error(error);
        return error;
    });
}

export async function finalizeAddContact(userRegistrationId) {

    const config = {
        method: 'POST',
        url: '/private/user-add-contact/finalize',
        headers: {
            'Content-Type': 'application/json'
        },
        data: {
            'userRegistrationId': userRegistrationId
        }
    }

    return await axios.request(config).then((response) => {
        return response;
    }, (error) => {
        console.error(error);
        return error;
    });

}
